import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Button, Card, Empty, Input, InputNumber, message, Select, Space, Table, Tag } from "antd";
import { EditOutlined, SaveOutlined, CloseOutlined, DownloadOutlined } from "@ant-design/icons";
import Header from "../components/header/Header";
import { apiRequest } from "../utils/apiClient";
import { setSelectedBranch } from "../redux/userSlice";

const { Search } = Input;

const StockManagementPage = () => {
  const { t } = useTranslation("pages");
  const { t: tCommon } = useTranslation("common");
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const selectedBranchId = user?.selectedBranchId || "";

  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [editingProductId, setEditingProductId] = useState(null);
  const [editingStock, setEditingStock] = useState(null);
  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);

  const isAdmin = user?.role !== "cashier";

  const effectiveBranchLabel = useMemo(() => {
    if (!isAdmin) {
      return t("stockBranchCurrent");
    }
    if (!selectedBranchId) {
      return t("stockBranchAll");
    }
    const branch = branches.find((b) => b._id === selectedBranchId);
    if (!branch) return t("stockBranchSelected");
    return `${branch.name} (${branch.location})`;
  }, [branches, isAdmin, selectedBranchId, t]);

  const fetchBranches = useCallback(async () => {
    if (!isAdmin) {
      setBranches([]);
      return;
    }
    try {
      setBranchesLoading(true);
      const data = await apiRequest("/api/branches", { method: "GET" });
      setBranches(Array.isArray(data) ? data : []);
    } catch {
      setBranches([]);
    } finally {
      setBranchesLoading(false);
    }
  }, [isAdmin]);

  const fetchCategories = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (isAdmin && selectedBranchId) {
        params.set("branchId", selectedBranchId);
      }
      const url = params.toString()
        ? `/api/categories/get-all?${params.toString()}`
        : "/api/categories/get-all";
      const data = await apiRequest(url, { method: "GET" });
      setCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      // keep silent for UX, optional filter
      console.error("Fetch categories for stock page failed:", error);
    }
  }, [isAdmin, selectedBranchId]);

  const isValidProductName = (rawTitle) => {
    const title = String(rawTitle ?? "").trim();
    if (!title) return false;
    return !["0", "3"].includes(title);
  };

  const fetchStock = useCallback(
    async (page = pagination.current, pageSize = pagination.pageSize) => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        params.set("page", String(page));
        params.set("limit", String(pageSize));
        const trimmedSearch = String(search || "").trim();
        if (trimmedSearch) {
          params.set("search", trimmedSearch);
        }
        if (categoryFilter) {
          params.set("category", categoryFilter);
        }
        if (isAdmin && selectedBranchId) {
          params.set("branchId", selectedBranchId);
        }
        params.set("sort", "nameAsc");

        const data = await apiRequest(
          `/api/products/stock-list?${params.toString()}`,
          {
            method: "GET",
          }
        );

        const incoming = Array.isArray(data?.products) ? data.products : [];
        const filtered = incoming.filter((p) => isValidProductName(p.title));

        setProducts(filtered);
        setPagination((prev) => ({
          ...prev,
          current: data?.page || page,
          pageSize,
          total: data?.total || 0,
        }));
      } catch (error) {
        console.error("Fetch stock list failed:", error);
        message.error(t("stockLoadFailed"));
      } finally {
        setLoading(false);
      }
    },
    [categoryFilter, isAdmin, search, selectedBranchId, t, pagination.current, pagination.pageSize]
  );

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchStock(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranchId]);

  const handleTableChange = (pager) => {
    const { current, pageSize } = pager;
    fetchStock(current, pageSize);
  };

  const onSearchSubmit = (value) => {
    setSearch(value);
    fetchStock(1, pagination.pageSize);
  };

  const handleEditClick = (record) => {
    setEditingProductId(record._id);
    setEditingStock(typeof record.totalStock === "number" ? record.totalStock : 0);
  };

  const handleCancelEdit = () => {
    setEditingProductId(null);
    setEditingStock(null);
  };

  const handleSaveStock = async (record) => {
    if (!editingProductId) return;
    const value = Number(editingStock);
    if (!Number.isFinite(value) || value < 0) {
      message.error(t("stockInvalidValue"));
      return;
    }
    try {
      const res = await apiRequest("/api/products/update-stock", {
        method: "PUT",
        body: JSON.stringify({
          productId: editingProductId,
          totalStock: value,
        }),
      });

      const updated = res?.product;
      setProducts((prev) =>
        prev.map((p) =>
          p._id === editingProductId
            ? {
                ...p,
                totalStock: updated?.totalStock ?? value,
              }
            : p
        )
      );
      message.success(t("stockUpdateSuccess"));
      handleCancelEdit();
    } catch (error) {
      console.error("Update stock failed:", error);
      message.error(t("stockUpdateFailed"));
    }
  };

  const escapeCsvCell = (value) => {
    const str = String(value ?? "").trim();
    const needsQuotes = /[\",\n\r]/.test(str);
    return needsQuotes ? `"${str.replace(/"/g, '""')}"` : str;
  };

  const handleExport = async () => {
    if (exporting) return;
    try {
      setExporting(true);
      const params = new URLSearchParams();
      params.set("page", "1");
      params.set("limit", "100000");
      const trimmedSearch = String(search || "").trim();
      if (trimmedSearch) {
        params.set("search", trimmedSearch);
      }
      if (categoryFilter) {
        params.set("category", categoryFilter);
      }
      if (isAdmin && selectedBranchId) {
        params.set("branchId", selectedBranchId);
      }
      params.set("sort", "nameAsc");

      const data = await apiRequest(
        `/api/products/stock-list?${params.toString()}`,
        {
          method: "GET",
        }
      );
      const rows = (Array.isArray(data?.products) ? data.products : []).filter(
        (p) => isValidProductName(p.title)
      );

      const businessName = user?.businessName || "Business";
      const dateStr = new Date().toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });

      const headerRows = [
        [escapeCsvCell(businessName)],
        [escapeCsvCell(t("stockExportTitle"))],
        [escapeCsvCell(dateStr)],
        [],
        [
          escapeCsvCell(t("productName")),
          escapeCsvCell(t("category")),
          escapeCsvCell(t("currentStock")),
          escapeCsvCell(t("stockBranchColumn")),
        ],
      ];

      const branchMap = new Map(
        branches.map((b) => [String(b._id), `${b.name} (${b.location})`])
      );

      const dataRows = rows.map((p) => {
        const branchLabel =
          (p.branchId && branchMap.get(String(p.branchId))) ||
          (selectedBranchId && branchMap.get(String(selectedBranchId))) ||
          t("stockBranchAll");
        return [
          escapeCsvCell(p.title ?? ""),
          escapeCsvCell(p.category ?? ""),
          escapeCsvCell(
            typeof p.totalStock === "number" ? String(p.totalStock) : "0"
          ),
          escapeCsvCell(branchLabel),
        ];
      });

      const csvContent = [...headerRows, ...dataRows]
        .map((row) => row.join(","))
        .join("\r\n");
      const blob = new Blob(["\uFEFF" + csvContent], {
        type: "text/csv;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `stock-report-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      message.success(t("stockExportSuccess", { count: rows.length }));
    } catch (error) {
      console.error("Export stock failed:", error);
      message.error(t("stockExportFailed"));
    } finally {
      setExporting(false);
    }
  };

  const columns = [
    {
      title: t("productName"),
      dataIndex: "title",
      key: "title",
      ellipsis: true,
    },
    {
      title: t("category"),
      dataIndex: "category",
      key: "category",
      ellipsis: true,
      render: (value) => (value ? <Tag>{value}</Tag> : <span>-</span>),
    },
    {
      title: t("price"),
      dataIndex: "price",
      key: "price",
      width: 120,
      align: "right",
      render: (value) =>
        typeof value === "number" ? value.toFixed(2) : value ?? "-",
    },
    {
      title: t("currentStock"),
      dataIndex: "totalStock",
      key: "totalStock",
      width: 160,
      align: "right",
      render: (value, record) => {
        const isEditing = editingProductId === record._id;
        if (!isEditing) {
          return typeof value === "number" ? value : "-";
        }
        return (
          <InputNumber
            min={0}
            value={editingStock}
            onChange={setEditingStock}
            style={{ width: "100%" }}
          />
        );
      },
    },
    {
      title: t("actions"),
      key: "actions",
      width: 180,
      render: (_, record) => {
        const isEditing = editingProductId === record._id;
        if (!isEditing) {
          return (
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEditClick(record)}
            >
              {t("editStock")}
            </Button>
          );
        }
        return (
          <Space>
            <Button
              type="primary"
              size="small"
              icon={<SaveOutlined />}
              onClick={() => handleSaveStock(record)}
            >
              {tCommon("save")}
            </Button>
            <Button
              size="small"
              icon={<CloseOutlined />}
              onClick={handleCancelEdit}
            >
              {tCommon("cancel")}
            </Button>
          </Space>
        );
      },
    },
  ];

  const hasData = products && products.length > 0;

  return (
    <>
      <Header />
      <div className="px-4 md:px-6 pb-20 max-w-5xl mx-auto">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-800">
              {t("stockManagement")}
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              {t("stockForBranch", { branch: effectiveBranchLabel })}
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {isAdmin && (
              <Select
                size="middle"
                className="min-w-[220px]"
                placeholder={t("selectBranch")}
                loading={branchesLoading}
                value={selectedBranchId || ""}
                onChange={(value) => {
                  dispatch(setSelectedBranch(value || ""));
                }}
                options={[
                  { value: "", label: t("allBranches") },
                  ...branches.map((b) => ({
                    value: b._id,
                    label: `${b.name} (${b.location})`,
                  })),
                ]}
              />
            )}
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExport}
              loading={exporting}
              disabled={exporting || !hasData}
            >
              {t("stockExportButton")}
            </Button>
          </div>
        </div>

        <Card className="mb-4">
          <div className="flex flex-col md:flex-row gap-3 md:items-center">
            <div className="flex-1 min-w-[220px]">
              <Search
                placeholder={t("stockSearchPlaceholder")}
                allowClear
                enterButton={
                  <Button type="primary" style={{ color: "#fff" }}>
                    {tCommon("search")}
                  </Button>
                }
                onSearch={onSearchSubmit}
                onChange={(e) => setSearch(e.target.value)}
                value={search}
              />
            </div>
            <div className="w-full md:w-60">
              <Select
                allowClear
                className="w-full"
                placeholder={t("stockCategoryFilterPlaceholder")}
                value={categoryFilter || undefined}
                onChange={(value) => {
                  setCategoryFilter(value || "");
                  fetchStock(1, pagination.pageSize);
                }}
                loading={false}
                options={[
                  ...categories.map((c) => ({
                    label: c.title,
                    value: c.title,
                  })),
                ]}
              />
            </div>
          </div>
        </Card>

        <Card>
          <Table
            rowKey="_id"
            loading={loading}
            dataSource={products}
            columns={columns}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
            }}
            onChange={handleTableChange}
            locale={{
              emptyText: (
                <Empty
                  description={
                    <span>{t("noStockRecords")}</span>
                  }
                />
              ),
            }}
          />
        </Card>
      </div>
    </>
  );
};

export default StockManagementPage;

